/*
 *  (c) 2015 - 2023 ENISCO by FORCAM GmbH
 */

import React from 'react'
import {App} from '../../model/apps'
import {Badge, Box, IconButton, Tooltip} from '@mui/material'
import {Favorite} from '../../model/favorites'
import {translateText, useLanguage, useThemeName} from '@enisco/mcc-react-lib'
import useCurrentApp from '../../hooks/useCurrentApp'
import {DefaultGroupIcon} from '../../icon/DefaultGroupIcon'
import {QuickLaunchType} from '../../model/quickLaunch'
import {useNavigate} from 'react-router-dom'
import {pathToApp, pathToFavorite} from '../../util/navigation'

interface Props {
  app: App
  registeredFavorites: Favorite[]
  quickLaunchBarType: QuickLaunchType
}

const QuickLaunchApp: React.FC<Props> = ({app, registeredFavorites, quickLaunchBarType}) => {
  const language = useLanguage()
  const [themeName] = useThemeName()
  const [currentApp] = useCurrentApp()
  const navigate = useNavigate()

  const getBadge = (appBadge: App) => {
    if (appBadge.favoriteId) {
      const favorite = registeredFavorites.find(
        (fav) => fav.id === appBadge.favoriteId
      )
      if (favorite) {
        if (favorite.user) {
          return themeName === 'dark'
            ? '/mcc-portal-ui/favorites/ic_favbadge_dark.svg'
            : '/mcc-portal-ui/favorites/ic_favbadge_light.svg'
        } else {
          return themeName === 'dark'
            ? '/mcc-portal-ui/favorites/ic_favbadge_global_dark.svg'
            : '/mcc-portal-ui/favorites/ic_favbadge_global_light.svg'
        }
      }
    }
    return ''
  }

  const getBorderSx = () => {
    if (currentApp && (currentApp.app === app.app && currentApp.module === app.module && currentApp.favoriteId === app.favoriteId)) {
      return quickLaunchBarType === 'left' ?
        {borderRight: 'solid', borderRightColor: 'primary.light'} :
        {borderLeft: 'solid', borderLeftColor: 'primary.light'}
    }
    return quickLaunchBarType === 'left' ?
      {paddingRight: '3px'} :
      {paddingLeft: '3px'}
  }

  return (
    <Box sx={getBorderSx()}>
      <Tooltip title={translateText(language, app.description)}>
        <IconButton
          data-testid={`QuickLaunchApp:${app.module}_${app.app}`}
          onClick={() => {
            if (app.favoriteId) {
              const favorite = registeredFavorites.find(
                (fav) => fav.id === app.favoriteId
              )
              if (favorite?.id) {
                navigate({pathname: pathToFavorite(favorite.id)})
              }
            } else {
              navigate({pathname: pathToApp(app.module, app.app)})
            }
          }}
          size="large"
        >
          <Badge
            badgeContent={<img src={getBadge(app)} width={'15px'} alt="" />}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          >
            {app.icon ? (
              <img src={app.icon} width={'32px'} height={'32px'} alt="" />
            ) : (
              <DefaultGroupIcon width={'32px'} height={'32px'} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export default QuickLaunchApp
